import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import Popover from '../../../components/molecules/Popover';
import OAuthLoginField from '../../../components/partials/OAuthLoginField';
import useGridExport, { GSHEET_NETWORK_DATA } from '../../hooks/useGridExport';
import css from './GridArtifact.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.GRID';
const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' };
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' };

const GridToolbar = ({ data, metadata, contentUrl, title, dateRangeLabel, translate }) => {
    const triggerRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const {
        copied,
        copyLoading,
        exportType,
        sheetsAuthActive,
        oauthRef,
        handleCopy,
        handleDownloadCsv,
        handleExportSheets,
        handleSheetsAuth,
        handleSheetsAuthError,
    } = useGridExport({ data, metadata, contentUrl, title, translate });

    // The Popover root is pointer-events:none, so outside clicks reach the page and
    // close the menu here; also close on Escape, scroll, or resize.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onPointerDown = e => {
            if (triggerRef.current?.contains(e.target)) return;
            if (e.target.closest(`.${css.exportPopoverRoot}`)) return;
            setMenuOpen(false);
        };
        const onKeyDown = e => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        const close = () => setMenuOpen(false);
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [menuOpen]);

    const toggleMenu = () => {
        if (exportType) return;
        setMenuOpen(open => !open);
    };

    const onDownloadCsv = () => {
        setMenuOpen(false);
        handleDownloadCsv();
    };

    const onExportSheets = () => {
        setMenuOpen(false);
        handleExportSheets();
    };

    return (
        <div className={css.gridToolbar}>
            {dateRangeLabel && <span className={css.dateRange}>{dateRangeLabel}</span>}
            <div className={css.toolbarActions}>
                <button
                    type="button"
                    className={css.toolbarButton}
                    aria-label={translate(`${TRANSLATION_PREFIX}.COPY_CSV_HOVER`)}
                    aria-busy={copyLoading}
                    onClick={handleCopy}
                >
                    <Icon
                        name={copyLoading ? 'loader' : copied ? 'check' : 'copy'}
                        size={IconSizes.SMALL}
                        className={copyLoading ? css.spinner : undefined}
                        hoverText={`${TRANSLATION_PREFIX}.COPY_CSV_HOVER`}
                        interactive={false}
                    />
                </button>

                <button
                    ref={triggerRef}
                    type="button"
                    className={classNames(css.toolbarButton, { [css.toolbarButtonActive]: menuOpen })}
                    aria-label={translate(`${TRANSLATION_PREFIX}.EXPORT_HOVER`)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-busy={!!exportType}
                    onClick={toggleMenu}
                >
                    <Icon
                        name={exportType ? 'loader' : 'download'}
                        size={IconSizes.SMALL}
                        className={exportType ? css.spinner : undefined}
                        hoverText={`${TRANSLATION_PREFIX}.EXPORT_HOVER`}
                        interactive={false}
                    />
                </button>

                <Popover
                    showPopover={menuOpen}
                    anchorEl={triggerRef.current}
                    anchorOrigin={MENU_ANCHOR_ORIGIN}
                    transformOrigin={MENU_TRANSFORM_ORIGIN}
                    rootClassName={css.exportPopoverRoot}
                    PaperProps={{ className: css.exportMenu }}
                    disableScrollLock
                    hideBackdrop
                    transitionDuration={140}
                >
                    <div className={css.exportMenuTitle}>{translate(`${TRANSLATION_PREFIX}.EXPORT_MENU_TITLE`)}</div>
                    <button
                        type="button"
                        className={css.exportTile}
                        aria-label={translate(`${TRANSLATION_PREFIX}.DOWNLOAD_CSV`)}
                        onClick={onDownloadCsv}
                    >
                        <span className={css.exportTileIcon}>
                            <Icon name="file-type-csv" size={IconSizes.MEDIUM} interactive={false} />
                        </span>
                        <span className={css.exportTileText}>
                            <span className={css.exportTileTitle}>
                                {translate(`${TRANSLATION_PREFIX}.DOWNLOAD_CSV`)}
                            </span>
                            <span className={css.exportTileDesc}>
                                {translate(`${TRANSLATION_PREFIX}.DOWNLOAD_CSV_DESC`)}
                            </span>
                        </span>
                    </button>
                    <button
                        type="button"
                        className={css.exportTile}
                        aria-label={translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET`)}
                        onClick={onExportSheets}
                    >
                        <span className={`${css.exportTileIcon} ${css.exportTileIconSheets}`}>
                            <Icon
                                name="table"
                                size={IconSizes.MEDIUM}
                                variant={IconVariants.SUCCESS}
                                interactive={false}
                            />
                        </span>
                        <span className={css.exportTileText}>
                            <span className={css.exportTileTitle}>
                                {translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET`)}
                            </span>
                            <span className={css.exportTileDesc}>
                                {translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET_DESC`)}
                            </span>
                        </span>
                    </button>
                </Popover>

                {sheetsAuthActive && (
                    <div style={{ display: 'none' }}>
                        <OAuthLoginField
                            ref={oauthRef}
                            networkData={GSHEET_NETWORK_DATA}
                            isValidated={false}
                            authPopupHandler={handleSheetsAuth}
                            usernameValue=""
                            onError={handleSheetsAuthError}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

GridToolbar.propTypes = {
    data: PropTypes.string,
    metadata: PropTypes.shape({
        is_truncated: PropTypes.bool,
        row_count: PropTypes.number,
        columns: PropTypes.arrayOf(PropTypes.string),
    }),
    contentUrl: PropTypes.string,
    title: PropTypes.string,
    dateRangeLabel: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

export default GridToolbar;
